'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  getConsent,
  saveConsent,
  acceptAll,
  ConsentChoices,
  ConsentRegion,
} from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  region: ConsentRegion;
  onSave: () => void;
  onClose: () => void;
}

interface ToggleProps {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description: string;
}

function Toggle({ id, checked, disabled, onChange, label, description }: ToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        id={id}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={`relative flex-shrink-0 mt-0.5 w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
          checked ? 'bg-primary' : 'bg-gray-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

export default function ConsentPreferenceCenter({
  region,
  onSave,
  onClose,
}: ConsentPreferenceCenterProps) {
  const existing = getConsent();
  const [choices, setChoices] = useState<ConsentChoices>({
    necessary: true,
    analytics: existing?.choices.analytics ?? false,
    marketing: existing?.choices.marketing ?? false,
    preferences: existing?.choices.preferences ?? false,
  });

  const dialogRef = useRef<HTMLDivElement>(null);

  // Trap focus inside modal
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const focusable = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    first?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSave = useCallback(() => {
    saveConsent(region, choices);
    onSave();
  }, [region, choices, onSave]);

  const handleAcceptAll = useCallback(() => {
    acceptAll(region);
    onSave();
  }, [region, onSave]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pref-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <h2 id="pref-title" className="text-base font-bold text-gray-900">
            Cookie Preferences
          </h2>
          <button
            onClick={onClose}
            aria-label="Close preferences"
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <p className="text-xs text-gray-500 mb-4 leading-relaxed">
            Choose which cookies you allow. Necessary cookies cannot be disabled as they are required
            for the platform to function. See our{' '}
            <a href="/cookies" className="text-primary underline hover:text-primary-dark">
              Cookie Policy
            </a>{' '}
            for details.
          </p>

          <Toggle
            id="toggle-necessary"
            checked
            disabled
            onChange={() => {}}
            label="Necessary"
            description="Required for authentication, session management, and core platform functionality. Cannot be disabled."
          />
          <Toggle
            id="toggle-analytics"
            checked={choices.analytics}
            onChange={(v) => setChoices((c) => ({ ...c, analytics: v }))}
            label="Analytics"
            description="Help us understand how CoLoco is used so we can improve it. Includes Google Analytics and platform analytics."
          />
          <Toggle
            id="toggle-marketing"
            checked={choices.marketing}
            onChange={(v) => setChoices((c) => ({ ...c, marketing: v }))}
            label="Marketing"
            description="Used to show relevant content and measure the effectiveness of our communications."
          />
          <Toggle
            id="toggle-preferences"
            checked={choices.preferences}
            onChange={(v) => setChoices((c) => ({ ...c, preferences: v }))}
            label="Preferences"
            description="Remember your settings and personalisation choices across sessions."
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 pb-5 pt-2">
          <button
            onClick={handleAcceptAll}
            className="text-xs font-medium text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
          >
            Accept All
          </button>
          <button
            onClick={handleSave}
            className="flex-1 bg-primary hover:bg-primary-dark text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors shadow-sm"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
